import { readonly, ref } from "vue";
import type {
  ConvexAuthRecoveryContext,
  ConvexAuthRecoveryResult,
  ConvexAuthTokenFetcher,
  ConvexVueClientLike,
  ConvexVueTelemetry,
} from "./types";

export interface ConvexAuthControllerOptions {
  client: ConvexVueClientLike;
  fetchToken: ConvexAuthTokenFetcher;
  verifyApiAuth: () => Promise<boolean>;
  clearTokenCache?: () => void;
  onLogout: (reason: string, error?: unknown) => void | Promise<void>;
  telemetry?: ConvexVueTelemetry;
  maxRecoveryAttempts?: number;
}

export function isConvexAuthError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /auth|token|jwt|unauth|forbidden|permission/i.test(message);
}

export function createConvexAuthController(options: ConvexAuthControllerOptions) {
  const isLoading = ref(true);
  const isAuthenticated = ref(false);
  const maxRecoveryAttempts = options.maxRecoveryAttempts ?? 1;
  let disposed = false;
  let inFlightToken: Promise<string | null> | null = null;
  let inFlightVerify: Promise<boolean> | null = null;

  async function fetchToken(args: { forceRefreshToken: boolean }) {
    if (args.forceRefreshToken) {
      options.clearTokenCache?.();
    }
    if (!args.forceRefreshToken && inFlightToken) {
      return inFlightToken;
    }
    inFlightToken = options.fetchToken(args).finally(() => {
      inFlightToken = null;
    });
    return inFlightToken;
  }

  function applyAuth() {
    disposed = false;
    isLoading.value = true;
    options.client.setAuth(fetchToken, (backendReportsIsAuthenticated) => {
      if (disposed) return;
      isAuthenticated.value = backendReportsIsAuthenticated;
      isLoading.value = false;
      options.telemetry?.breadcrumb?.({
        operation: "convex.auth_state_changed",
        level: backendReportsIsAuthenticated ? "info" : "warning",
        auth: { isAuthenticated: backendReportsIsAuthenticated },
      });
    });
  }

  function clearAuth() {
    disposed = true;
    inFlightToken = null;
    inFlightVerify = null;
    options.clearTokenCache?.();
    options.client.clearAuth?.();
    isAuthenticated.value = false;
    isLoading.value = false;
  }

  async function verifyApiAuth() {
    if (!inFlightVerify) {
      inFlightVerify = options.verifyApiAuth().finally(() => {
        inFlightVerify = null;
      });
    }
    return inFlightVerify;
  }

  async function recoverFromAuthError(context: ConvexAuthRecoveryContext): Promise<ConvexAuthRecoveryResult> {
    if (!isConvexAuthError(context.error)) {
      return { didRecover: false, shouldLogout: false, reason: "token-refresh-failed" };
    }

    if (context.recoveryAttempt >= maxRecoveryAttempts) {
      options.telemetry?.error?.(context.error, {
        operation: `${context.operation}.auth_recovery_max_attempts`,
        level: "error",
        functionName: context.functionName,
        auth: { recoveryAttempt: context.recoveryAttempt, isRecoverable: false },
      });
      return { didRecover: false, shouldLogout: false, reason: "max-attempts" };
    }

    let apiAuthValid = false;
    try {
      apiAuthValid = await verifyApiAuth();
    } catch (error) {
      options.telemetry?.error?.(error, {
        operation: `${context.operation}.api_auth_verify_failed`,
        level: "error",
        functionName: context.functionName,
        auth: { recoveryAttempt: context.recoveryAttempt },
      });
      return { didRecover: false, shouldLogout: false, reason: "verify-failed" };
    }

    if (!apiAuthValid) {
      clearAuth();
      await options.onLogout("api-auth-invalid", context.error);
      return { didRecover: false, shouldLogout: true, reason: "api-auth-invalid" };
    }

    options.clearTokenCache?.();
    const token = await fetchToken({ forceRefreshToken: true });
    const didRecover = Boolean(token);
    options.telemetry?.breadcrumb?.({
      operation: `${context.operation}.auth_recovery_finished`,
      level: didRecover ? "info" : "warning",
      functionName: context.functionName,
      auth: { didRecover, forceRefreshToken: true, recoveryAttempt: context.recoveryAttempt + 1 },
    });
    if (!didRecover) {
      clearAuth();
      await options.onLogout("convex-token-refresh-failed", context.error);
    }
    return {
      didRecover,
      shouldLogout: !didRecover,
      reason: didRecover ? "recovered" : "token-refresh-failed",
    };
  }

  function installFocusAuthCheck(target: Pick<Window, "addEventListener" | "removeEventListener"> = window) {
    const onFocus = async () => {
      if (!isAuthenticated.value) return;
      try {
        const valid = await verifyApiAuth();
        if (!valid) {
          await options.onLogout("api-auth-invalid-on-focus");
        }
      } catch (error) {
        options.telemetry?.error?.(error, {
          operation: "convex.auth_focus_check_failed",
          level: "error",
        });
      }
    };
    target.addEventListener("focus", onFocus);
    return () => target.removeEventListener("focus", onFocus);
  }

  return {
    authState: {
      isLoading: readonly(isLoading),
      isAuthenticated: readonly(isAuthenticated),
    },
    applyAuth,
    clearAuth,
    recoverFromAuthError,
    installFocusAuthCheck,
    fetchToken,
    verifyApiAuth,
  };
}
